import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/icon'
import { useAnchoredPopover } from '@/shared/ui/popover'

// Dropdown действий (⋯ на карточке, меню пользователя). Anchored popover
// (top-layer, не двигает layout внутри <dialog>): закрытие по Escape и клику вне,
// фокус первого пункта при открытии.

export interface MenuItem {
  label: string
  icon?: IconName
  onSelect: () => void
  danger?: boolean
  disabled?: boolean
}

interface MenuProps {
  trigger: ReactNode
  items: MenuItem[]
  align?: 'start' | 'end'
  triggerClassName?: string
  'aria-label'?: string
}

export function Menu({
  trigger,
  items,
  align = 'end',
  triggerClassName,
  'aria-label': ariaLabel,
}: MenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const {
    anchorRef: triggerRef,
    popoverRef: menuRef,
    popoverProps,
  } = useAnchoredPopover<HTMLButtonElement, HTMLDivElement>({ open, align })
  const menuId = useId()

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])')?.focus()
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, menuRef])

  return (
    <div ref={rootRef} className="inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center rounded-control text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
          triggerClassName,
        )}
      >
        {trigger}
      </button>
      {open && (
        <div
          ref={menuRef}
          {...popoverProps}
          role="menu"
          id={menuId}
          className="m-0 min-w-44 overflow-hidden rounded-card border border-border-default bg-bg-surface py-1 shadow-lg shadow-black/40"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false)
                item.onSelect()
              }}
              className={cn(
                'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors disabled:opacity-40',
                item.danger
                  ? 'text-accent hover:bg-accent/10'
                  : 'text-text-secondary hover:bg-bg-raised hover:text-text-primary',
              )}
            >
              {item.icon && <Icon name={item.icon} size={16} className="shrink-0" />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
